/**
 * The bounded "free set" and its selection helpers — the single source that keeps
 * every free-user content door (Quick Set, Custom Test, Flashcards, Articles)
 * drawing from ONE agreed pool instead of the full bundle. Free By-Topic study
 * already serves this exact slice (`buildByTopicStudySession` in ./sessions.js);
 * these functions lift that definition so the other doors can't drift from it.
 *
 * Pure and platform-agnostic: callers pass already-fetched plain objects (the
 * bundled content list, the domain list with its per-domain caps, the user's
 * per-question score records). No SDKs, no UI, no navigation.
 *
 * DRIFT NOTE (accepted v1 behavior, NOT a bug): the free set is defined as the
 * "first N items per domain in BUNDLE ORDER" (mirroring By-Topic's
 * `slice(0, num_questions)`). A content update that inserts or reorders questions
 * therefore ROTATES the free set and can expose previously-unseen questions to
 * free users (and correspondingly retire others). This is intentional for v1 —
 * the free door and By-Topic must agree, and By-Topic already has this property.
 */

import { computeQuestionPools } from '../readiness/questionPools';

// Dev-only guard so a missing cap surfaces once (see filterToFreeSet). Module-level
// so it warns at most once per capField per session, not on every render.
const _checkedCapFields = new Set();

/**
 * The bounded free set for a content type: the first `cap` items of each gated
 * domain (in bundle order), unioned across domains. `cap` is read from each
 * domain row's `capField` (default `num_questions`; use `num_flashcards` /
 * `num_articles` for those doors).
 *
 * Semantics pinned to By-Topic's `slice(0, cap)`:
 *  - `cap == null` / undefined for a domain → that domain is UNCAPPED (all its
 *    items), exactly like `Array.slice(0, undefined)`.
 *  - `cap === 0` → zero free items for that domain.
 *  - An item whose `topic_id` is not a gated domain is EXCLUDED from the free set
 *    (By-Topic never surfaces it either — there is no domain row to open it under).
 *  - Bundle order is preserved; ids are compared type-insensitively via String().
 *
 * @param {Array}  items      bundled content: [{ id, topic_id, ... }]
 * @param {Array}  domainList APP_DOMAIN_LIST rows: [{ id, num_questions, num_flashcards, num_articles }]
 * @param {string} [capField='num_questions']
 * @returns {Array} the free-set subset of `items`, in bundle order
 */
export function filterToFreeSet(items, domainList, capField = 'num_questions') {
  const caps = new Map((domainList ?? []).map((d) => [String(d?.id), d?.[capField]]));

  // Dev-only misconfig surface (once per capField). A gated domain missing its cap
  // is UNCAPPED (whole content free) — documented parity with By-Topic, but since
  // FREE_SET_ENFORCED ships ON everywhere, a domain added without num_questions /
  // num_flashcards / num_articles would silently reopen the leak this closes.
  if (
    !_checkedCapFields.has(capField) &&
    typeof process !== 'undefined' &&
    process?.env?.NODE_ENV === 'development' &&
    typeof console !== 'undefined'
  ) {
    _checkedCapFields.add(capField);
    const uncapped = (domainList ?? []).filter((d) => d?.[capField] == null).map((d) => d?.id);
    if (uncapped.length) {
      console.warn(
        `[freeContentSet] ${capField} is undefined for domain(s) [${uncapped.join(', ')}] — ` +
          `those domains are UNCAPPED for free users (entire content exposed). Set ${capField} on each gated domain.`
      );
    }
  }

  const takenPerDomain = new Map();
  const out = [];
  for (const item of items ?? []) {
    const key = String(item?.topic_id);
    if (!caps.has(key)) continue; // topic not a gated domain → not part of the free set
    const cap = caps.get(key);
    const taken = takenPerDomain.get(key) ?? 0;
    if (cap == null || taken < cap) {
      out.push(item);
      takenPerDomain.set(key, taken + 1);
    }
  }
  return out;
}

/**
 * The set of question ids the user has actually ANSWERED (times_answered >= 1).
 * Stub/bookmark records (times_answered === 0) are NOT answers — same rule as
 * `computeQuestionPools`. Returned as a Set<string> for O(1) membership.
 *
 * @param {Array} scores  questionScore.all_questions_scores
 * @returns {Set<string>}
 */
export function answeredIdSet(scores) {
  const set = new Set();
  for (const s of scores ?? []) {
    if ((s?.times_answered ?? 0) >= 1) set.add(String(s.question_id));
  }
  return set;
}

/**
 * Deterministic selection for the "give me new questions" flows (Quick Set,
 * default-priority Custom Test) over an EFFECTIVE pool = free set ∩ selected
 * domains. Fills UNSEEN items first (bundle order), then backfills already-answered
 * items to reach `count`.
 *
 * Contract:
 *  - Returns exactly `min(count, pool.length)` items — a SHORT result only when
 *    the whole effective pool is smaller than the request.
 *  - NEVER duplicates an item within the result (the pool holds unique ids).
 *  - Order is deterministic (unseen-first, each group in bundle order); callers
 *    that want randomized presentation shuffle the returned array themselves —
 *    the unseen-first PRIORITY (which items are included when pool > count) is
 *    preserved regardless of any later shuffle.
 *
 * @param {Array} effectivePool  the free set already narrowed to the selected domains
 * @param {Set<string>|Array} answeredIds  ids the user has answered (from answeredIdSet)
 * @param {number} count         requested test size
 * @returns {Array} min(count, pool.length) items, unseen-first
 */
export function selectFreeWithBackfill(effectivePool, answeredIds, count) {
  const pool = effectivePool ?? [];
  const n = Math.max(0, Math.min(count ?? 0, pool.length));
  const answered =
    answeredIds instanceof Set ? answeredIds : new Set((answeredIds ?? []).map((x) => String(x)));
  const unseen = [];
  const seen = [];
  for (const item of pool) {
    (answered.has(String(item?.id)) ? seen : unseen).push(item);
  }
  return [...unseen, ...seen].slice(0, n);
}

/**
 * True when a free user has NO unseen questions left in their free set — the
 * signal that drives the persistent home "exhaustion" card. Computed from state
 * (never a stored flag) so it auto-clears if a content update rotates the free
 * set to expose new unseen questions, and reappears on re-exhaustion.
 *
 * An empty free set returns false (nothing to exhaust → no card).
 *
 * @param {Array} freeSet   filterToFreeSet(questionsList, domainList) output
 * @param {Array} scores    questionScore.all_questions_scores
 * @param {Array} [bookmarks] profile.question_bookmarks (accepted for API symmetry;
 *                            exhaustion depends only on answered coverage)
 * @returns {boolean}
 */
export function isFreeQuestionSetExhausted(freeSet, scores, bookmarks) {
  const set = freeSet ?? [];
  if (set.length === 0) return false;
  const topicIds = [...new Set(set.map((q) => q?.topic_id))];
  const pools = computeQuestionPools(set, scores, topicIds, { bookmarkIds: bookmarks ?? [] });
  return pools.unanswered.length === 0;
}

/**
 * The copy for the shared "you've completed your free questions" exhaustion
 * modal — one source so the web and mobile twins stay byte-identical (a drift
 * would ship two different upgrade promises). Pure: the caller passes the
 * already-resolved brand facts; this decides wording, not rendering.
 *
 * The cross-exam upgrade promise ("plus full access to all N certification
 * exams in the app") is included ONLY when upgrading actually unlocks every
 * exam — i.e. a genuine multi-exam bundle that is NOT white-label. White-label
 * bundles (e.g. trainingcamp) ship IS_BUNDLE_APP=true with multiple exams but
 * strictly per-exam entitlements and no paywall, so the whole-app promise would
 * be false there.
 *
 * @param {Object}  p
 * @param {number}  p.freeCount     size of the bounded free set the user has seen (N)
 * @param {number}  p.totalCount    full question-bank size for this exam (exact)
 * @param {number}  [p.examCount=1] number of exams in the app (APP_BUNDLES.length)
 * @param {boolean} [p.isBundleApp] IS_BUNDLE_APP
 * @param {boolean} [p.isWhiteLabel] IS_WHITE_LABEL
 * @returns {{ title: string, description: string, confirmText: string }}
 */
export function exhaustionModalCopy({
  freeCount,
  totalCount,
  examCount = 1,
  isBundleApp = false,
  isWhiteLabel = false,
} = {}) {
  const showCrossExam = Boolean(isBundleApp) && !isWhiteLabel && Number(examCount) > 1;
  const crossExam = showCrossExam
    ? `, plus full access to all ${examCount} certification exams in the app`
    : '';

  return {
    title: "You've completed your free questions",
    description:
      `You've seen all ${freeCount} free questions for this exam. You'll now be practicing ` +
      `questions you've already seen. Unlock all ${totalCount} questions to keep progressing${crossExam}.`,
    confirmText: `Unlock all ${totalCount} questions`,
  };
}
